import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { DebugContextType } from './types';

const DebugContext = createContext<DebugContextType | undefined>(undefined);

const DEBUG_STORAGE_KEY = 'flexwise-debug-mode';
const DEBUG_PASSWORD = 'G:2j5v.M9Q3';

interface DebugProviderProps {
  children: React.ReactNode;
}

export function DebugProvider({ children }: DebugProviderProps) {
  const [isDebugMode, setIsDebugMode] = useState<boolean>(() => {
    // Initialize from localStorage or URL param
    try {
      const stored = localStorage.getItem(DEBUG_STORAGE_KEY);
      if (stored === 'true') return true;
      // Only allow URL-based debug enabling in non-production builds
      if (import.meta.env.DEV) {
        const params = new URLSearchParams(window.location.search);
        if (params.get('debug') === '1' || params.get('__fwDebug') === '1') return true;
      }
      return false;
    } catch {
      return false;
    }
  });

  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Persist debug mode to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DEBUG_STORAGE_KEY, isDebugMode.toString());
    } catch {
      // Ignore localStorage errors
    }
  }, [isDebugMode]);

  // Key combination listener (Ctrl+Shift+D, Ctrl+Alt+Shift+D, or Meta+Shift+D)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isD = (event.key || '').toLowerCase() === 'd';
      const ctrlOrMeta = !!(event.ctrlKey || event.metaKey);
      const hasShift = !!event.shiftKey;
      const altOptional = !!event.altKey; // allowed but not required

      if (isD && hasShift && ctrlOrMeta) {
        // Support with or without Alt (so both Ctrl+Shift+D and Ctrl+Alt+Shift+D work)
        event.preventDefault();
        if (isDebugMode) {
          exitDebugMode();
        } else {
          setShowPasswordModal(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDebugMode]);

  const verifyPassword = useCallback((password: string): boolean => {
    return password === DEBUG_PASSWORD;
  }, []);

  const enterDebugMode = useCallback(() => {
    setIsDebugMode(true);
    setShowPasswordModal(false);
  }, []);

  const exitDebugMode = useCallback(() => {
    setIsDebugMode(false);
    setShowPasswordModal(false);
  }, []);

  const toggleDebugMode = useCallback(() => {
    if (isDebugMode) {
      exitDebugMode();
    } else {
      setShowPasswordModal(true);
    }
  }, [isDebugMode, exitDebugMode]);


  const value: DebugContextType = {
    isDebugMode,
    toggleDebugMode,
    enterDebugMode,
    exitDebugMode,
    showPasswordModal,
    setShowPasswordModal,
    verifyPassword,
  };

  return (
    <DebugContext.Provider value={value}>
      {children}
    </DebugContext.Provider>
  );
}

export function useDebug(): DebugContextType {
  const context = useContext(DebugContext);
  if (context === undefined) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
}

// Helper hook for components to get their debug ID
export function useDebugId(componentName: string): string {
  const { generateDebugId } = require('./debugIds');
  return generateDebugId(componentName);
}
